import voiceThumb from "../../Assets/Projects/Thumbnails/voice-agent.jpg";
import ticketThumb from "../../Assets/Projects/Thumbnails/ticketing-assistant.jpg";
import skiftThumb from "../../Assets/Projects/Thumbnails/skift.jpg";
import poseThumb from "../../Assets/Projects/Thumbnails/human-pose-estimation.jpg";
import transcriptThumb from "../../Assets/Projects/Thumbnails/transcript-checklist-qa.jpg";
import jobThumb from "../../Assets/Projects/Thumbnails/job-tracker.jpg";
import contentThumb from "../../Assets/Projects/Thumbnails/ai-content-automation.jpg";
import expenseThumb from "../../Assets/Projects/Thumbnails/expensehive.jpg";

import voiceSnap from "../../Assets/Projects/Screenshots/voice-agent.png";
import ticketSnap from "../../Assets/Projects/Screenshots/ticketing-assistant.png";
import skiftAiSnap from "../../Assets/Projects/Screenshots/skift-ai.png";
import skiftScheduleSnap from "../../Assets/Projects/Screenshots/skift-schedule.png";
import skiftDashboardSnap from "../../Assets/Projects/Screenshots/skift-dashboard.png";
import poseSnap from "../../Assets/Projects/Screenshots/human-pose-estimation.png";
import transcriptSnap from "../../Assets/Projects/Screenshots/transcript-checklist-qa.png";
import jobSnap from "../../Assets/Projects/Screenshots/job-tracker.png";
import contentSnap from "../../Assets/Projects/Screenshots/ai-content-automation.png";
import expenseSnap from "../../Assets/Projects/Expensehive.jpg";

/**
 * @typedef {Object} PortfolioProject
 * @property {string} slug Stable route identifier.
 * @property {string} name Public project name.
 * @property {number} order Directory order.
 * @property {boolean} featured Whether the project appears on the homepage.
 * @property {string} category Short discipline label.
 * @property {string} context Project context or status.
 * @property {string} summary One-sentence product promise.
 * @property {string} statement Editorial headline.
 * @property {string} accent Project-specific accent colour.
 * @property {{src: string|null, alt: string, kind: string}} visual Preview asset metadata.
 * @property {{url: string|null, label: string, status: "public"|"coming-soon"}} repository
 * @property {{problem:string,audience:string,role:string,workflow:string[],architecture:string[],decisions:string[],constraints:string[],outcomes:string[],learnings:string[]}} caseStudy
 * @property {{scenario:string,inputLabel:string,input:string,stages:string[],resultTitle:string,result:Array<{label:string,value:string,tone?:string}>}} simulation
 */

/** @type {PortfolioProject[]} */
export const projectCatalog = [
  {
    slug: "voice-agent",
    name: "Voice Agent",
    order: 1,
    featured: true,
    category: "Conversational AI",
    context: "Healthcare reception · Voice + RAG",
    summary: "A voice-first healthcare receptionist that answers practice questions, remembers context, and knows when to escalate.",
    statement: "A useful voice agent knows its boundaries.",
    accent: "#9ec6ff",
    visual: { src: voiceThumb, alt: "Voice Agent conversational AI reception interface", kind: "Conversational AI" },
    repository: { url: "https://github.com/itscharanteja/AMME-VoiceAgent", label: "View repository", status: "public" },
    stack: ["React", "Vite", "FastAPI", "ChromaDB", "Amazon Bedrock", "Nova Lite", "Nova Sonic"],
    caseStudy: {
      problem: "Practice reception teams repeatedly answer policy and appointment questions, while clinical requests require careful boundaries and reliable escalation.",
      audience: "Patients looking for fast administrative help and healthcare teams that need a safe first line of response.",
      role: "Designed and built the browser experience, FastAPI orchestration, retrieval layer, session memory, voice lifecycle, and healthcare guardrails.",
      workflow: ["Capture a caller's voice", "Transcribe and retain conversational context", "Retrieve practice knowledge", "Apply healthcare safety rules", "Speak a grounded answer or escalate"],
      architecture: ["React + Vite voice interface", "FastAPI voice-agent orchestrator", "ChromaDB semantic retrieval", "Amazon Bedrock Nova reasoning and speech services"],
      blueprint: {
        topology: "Real-Time Voice + Grounded RAG Pipeline",
        tiers: [
          {
            label: "Frontend (React + Vite)",
            nodes: [
              { title: "Voice Input (STT)", sub: "Web Speech API · 16kHz PCM", tech: "webspeech", edgeToNext: { label: "Live Stream", dir: "forward" } },
              { title: "QuantumLoop UI & Visualizer", sub: "Orb, Waveform & Transcript", tech: "react", primary: true },
              { title: "Voice Playback (TTS)", sub: "Browser SpeechSynthesis", tech: "speechsynthesis" },
            ],
          },
          {
            label: "Backend (Python FastAPI)",
            nodes: [
              { title: "Session Memory", sub: "Multi-Turn Context State", tech: "fastapi", edgeToNext: { label: "Turn History", dir: "both" } },
              { title: "VoiceAgent Orchestrator", sub: "Prompt Assembly & Guardrails", tech: "python", primary: true, edgeToNext: { label: "Semantic Search", dir: "both" } },
              { title: "ChromaDB Vector Store (RAG)", sub: "Practice Policies & Insurance KB", tech: "chromadb" },
            ],
          },
          {
            label: "AI Services (Amazon Bedrock)",
            nodes: [
              { title: "Nova Sonic (STT Fallback)", sub: "Server-Side Audio Transcription", tech: "aws", edgeToNext: { label: "Transcribed Text", dir: "forward", dashed: true } },
              { title: "Nova Lite (Reasoning)", sub: "Grounded 1–2 Sentence Reply", tech: "aws", primary: true, edgeToNext: { label: "Verified Reply", dir: "forward" } },
              { title: "Clinical Safety Boundary", sub: "Emergency Escalation & Mic Release", tech: "guardrails" },
            ],
          },
        ],
        connectors: [
          [
            { label: "Audio WAV (Fallback)", dir: "down", dashed: true },
            { label: "HTTP POST /api/chat", dir: "both", accent: true },
            { label: "Spoken Audio Output", dir: "up" },
          ],
          [
            { label: "Audio WAV Stream", dir: "down", dashed: true },
            { label: "Converse API", dir: "both", accent: true },
            { label: "Spoken Response", dir: "up" },
          ],
        ],
      },
      decisions: ["Ground administrative answers in a practice knowledge base", "Keep clinical diagnosis and prescribing outside the agent's scope", "Release microphone tracks cleanly when a call ends"],
      constraints: ["Real microphone and Bedrock calls require local credentials", "Speech recognition quality varies with the browser and environment"],
      outcomes: ["Supports multi-turn practice questions", "Detects emergency language and routes the caller toward human or emergency support"],
      learnings: ["Voice UX depends as much on turn-taking and recovery as model quality", "Safety language should be visible in the experience, not hidden in prompts"],
      snapshots: [
        {
          src: voiceSnap,
          alt: "Voice Agent call workspace with active call transcription and patient queue",
          caption: "Live voice receptionist workspace with active call transcription, audio waveform, and policy triage"
        }
      ],
    },
    simulation: {
      scenario: "A caller needs to know whether the practice accepts walk-ins for a recurring prescription question.",
      inputLabel: "Fixed caller message",
      input: "Hi, I need a repeat prescription today. Can I just walk in, or do I need to book?",
      stages: ["Listening to the caller", "Transcribing the request", "Retrieving practice policy", "Applying healthcare guardrails", "Preparing a grounded response"],
      resultTitle: "Administrative answer, safely bounded",
      result: [
        { label: "Intent", value: "Repeat prescription policy" },
        { label: "Retrieved context", value: "Repeat prescriptions require a request in advance; walk-ins are not guaranteed." },
        { label: "Agent response", value: "Please submit a repeat-prescription request through the practice or call reception before visiting. If you have urgent symptoms, contact an appropriate medical service." },
        { label: "Safety", value: "No diagnosis or medication advice given", tone: "positive" },
      ],
    },
  },
  {
    slug: "ticketing-assistant",
    name: "Ticketing Assistant",
    order: 2,
    featured: true,
    category: "Applied AI",
    context: "Internal support · RAG + microservices",
    summary: "An internal support workflow that connects ticket intake, grounded resolution, notifications, and human escalation.",
    statement: "Give every support question the context it needs.",
    accent: "#9ce9e1",
    visual: { src: ticketThumb, alt: "Ticketing Assistant intelligent support flow", kind: "Applied AI" },
    repository: { url: "https://github.com/itscharanteja/AI_Ticketing_assistant", label: "View repository", status: "public" },
    stack: ["React", "Node.js", "Express", "PostgreSQL", "Sequelize", "Claude", "Docker", "GitHub Actions"],
    caseStudy: {
      problem: "Internal support teams lose time triaging repeated questions and connecting each request to the correct documentation.",
      audience: "Employees raising support requests and operations teams managing resolution quality and escalations.",
      role: "Built the React interface, ticket and AI services, custom retrieval flow, email notifications, automated testing, and containerized development setup.",
      workflow: ["Submit a support request", "Classify the issue", "Search the knowledge base", "Draft a grounded resolution", "Notify the requester or escalate"],
      architecture: ["React ticket dashboard", "Express ticket service", "Claude-powered AI service with SmartRAGEngine", "PostgreSQL via Sequelize", "Nodemailer notifications"],
      blueprint: {
        topology: "Decoupled Microservice + SmartRAG Support Pipeline",
        tiers: [
          {
            label: "Frontend (React Support Portal)",
            nodes: [
              { title: "Ticket Intake Form", sub: "Issue & Category Capture", tech: "react", edgeToNext: { label: "Submit Ticket", dir: "forward" } },
              { title: "Support Dashboard UI", sub: "Resolution & Status Workspace", tech: "react", primary: true },
              { title: "Operator Escalation Queue", sub: "Human Review & Handoff", tech: "users" },
            ],
          },
          {
            label: "Backend (Node.js + Express Microservices)",
            nodes: [
              { title: "PostgreSQL + Sequelize", sub: "Tickets & Audit Persistence", tech: "postgresql", edgeToNext: { label: "CRUD State", dir: "both" } },
              { title: "Express Ticket Service", sub: "Lifecycle & Routing Orchestrator", tech: "express", primary: true, edgeToNext: { label: "SmartRAG Query", dir: "both" } },
              { title: "SmartRAGEngine", sub: "Internal Docs & Confidence Ranker", tech: "rag" },
            ],
          },
          {
            label: "AI & Delivery Services (Claude + Notifications)",
            nodes: [
              { title: "Docker + GitHub Actions", sub: "Isolated Service CI Suite", tech: "docker", edgeToNext: { label: "Verified Build", dir: "forward", dashed: true } },
              { title: "Anthropic Claude Service", sub: "Grounded Troubleshooting Draft", tech: "claude", primary: true, edgeToNext: { label: "Resolution Event", dir: "forward" } },
              { title: "Nodemailer Dispatch", sub: "Requester & Ops Email Alerts", tech: "email" },
            ],
          },
        ],
        connectors: [
          [
            { label: "Ticket Metadata", dir: "down" },
            { label: "HTTP REST API", dir: "both", accent: true },
            { label: "Low-Confidence Escalation", dir: "up", dashed: true },
          ],
          [
            { label: "Service Contracts", dir: "both", dashed: true },
            { label: "Grounded Prompt + Context", dir: "both", accent: true },
            { label: "SMTP Notification", dir: "up" },
          ],
        ],
      },
      decisions: ["Separate ticket CRUD from AI resolution", "Keep escalation available when retrieved context is insufficient", "Test services independently inside a microservice structure"],
      constraints: ["A production deployment needs configured AI, email, and database credentials", "Knowledge quality directly affects suggested resolutions"],
      outcomes: ["Repository includes automated service and interface tests", "The workflow supports both immediate AI answers and human escalation"],
      learnings: ["Retrieval confidence is a workflow decision, not merely a model score", "Operational notifications complete the experience beyond the generated answer"],
      snapshots: [
        {
          src: ticketSnap,
          alt: "Ticketing assistant queue and resolution dashboard",
          caption: "Live support desk workspace with category intake, confidence rankings, and grounded resolution draft"
        }
      ],
    },
    simulation: {
      scenario: "An employee cannot access the payroll portal after a password change.",
      inputLabel: "Fixed support ticket",
      input: "Payroll portal says ‘session invalid’ after I reset my password. I can sign in to email normally.",
      stages: ["Classifying the ticket", "Searching support knowledge", "Checking resolution confidence", "Drafting next steps", "Preparing notification"],
      resultTitle: "Resolution ready for review",
      result: [
        { label: "Category", value: "Access · Payroll portal" },
        { label: "Priority", value: "Normal — no payroll deadline reported" },
        { label: "Suggested resolution", value: "Sign out of the portal, clear its saved session, then sign in with the updated credentials. Escalate to Identity Support if the error remains." },
        { label: "Route", value: "AI response with human escalation available", tone: "positive" },
      ],
    },
  },
  {
    slug: "skift",
    name: "Skift",
    order: 3,
    featured: true,
    category: "Product Engineering",
    context: "Restaurant operations · Scheduling AI",
    summary: "A bilingual operations workspace for restaurant schedules, attendance, staffing constraints, and daily coordination.",
    statement: "A schedule is a promise between people and operations.",
    accent: "#f0b47a",
    visual: { src: skiftThumb, alt: "Skift restaurant operations scheduling dashboard", kind: "Product Engineering" },
    repository: { url: "https://github.com/itscharanteja/scheduler", label: "View repository", status: "public" },
    stack: ["Next.js", "TypeScript", "Supabase", "PostgreSQL", "Claude", "OpenAI", "Tailwind CSS"],
    caseStudy: {
      problem: "Restaurant managers need to coordinate weekly coverage, employee contracts, attendance, labor cost, and operational communication without fragmented tools.",
      audience: "Swedish quick-service restaurant managers, shift leads, regional managers, and employees.",
      role: "Built the scheduling product, role-aware navigation, location isolation, AI schedule generation, attendance kiosk, chat, inventory, and operational modules.",
      workflow: ["Choose a location and week", "Load contracts and availability", "Generate a draft schedule", "Review labor-law and hour warnings", "Apply shifts and monitor attendance"],
      architecture: ["Next.js App Router application", "Supabase Postgres and authentication", "Server-verified role and location access", "Claude/OpenAI schedule generation routes"],
      blueprint: {
        topology: "Multi-Location SSR + Privacy-Preserving AI Scheduling",
        tiers: [
          {
            label: "Frontend (Next.js App Router + TypeScript)",
            nodes: [
              { title: "Availability & Contracts", sub: "Bilingual EN / SV Input", tech: "typescript", edgeToNext: { label: "Staff Rules", dir: "forward" } },
              { title: "Weekly Shift Planner UI", sub: "Draft Grid & Coverage Warnings", tech: "nextjs", primary: true },
              { title: "Attendance Kiosk & Ops", sub: "Clock-In, Team Chat & Inventory", tech: "attendance" },
            ],
          },
          {
            label: "Backend (Next.js Server Routes + Guardrails)",
            nodes: [
              { title: "Session & Location Guard", sub: "Server-Verified Tenant Scope", tech: "auth", edgeToNext: { label: "Authorized Scope", dir: "both" } },
              { title: "Scheduling Orchestrator", sub: "Constraint & Labor-Rule Engine", tech: "nextjs", primary: true, edgeToNext: { label: "Tokenize Staff", dir: "both" } },
              { title: "PII Anonymizer", sub: "Replaces Names with Tokens", tech: "privacy" },
            ],
          },
          {
            label: "AI & Data Services (Supabase + Claude / OpenAI)",
            nodes: [
              { title: "Supabase Auth & RLS", sub: "Role & Location Isolation", tech: "supabase", edgeToNext: { label: "Verified Policies", dir: "forward" } },
              { title: "Claude / OpenAI Planner", sub: "Anonymized Draft Generation", tech: "openai", primary: true, edgeToNext: { label: "Draft Shifts", dir: "forward" } },
              { title: "Supabase PostgreSQL", sub: "Reversible Drafts & Live Rota", tech: "postgresql" },
            ],
          },
        ],
        connectors: [
          [
            { label: "Session Cookie", dir: "down" },
            { label: "Typed Server Actions", dir: "both", accent: true },
            { label: "Approved Schedule Sync", dir: "up" },
          ],
          [
            { label: "Tenant RLS Check", dir: "both" },
            { label: "Anonymized Prompt JSON", dir: "both", accent: true },
            { label: "Commit Approved Rota", dir: "both" },
          ],
        ],
      },
      decisions: ["Anonymize employee names before AI scheduling", "Treat generated shifts as reviewable drafts", "Resolve location access from the verified session instead of client input"],
      constraints: ["Managers remain responsible for approving schedules", "Labor-law warnings are guidance rather than legal advice"],
      outcomes: ["Unifies scheduling, attendance, communication, inventory, and daily operations", "Supports English and Swedish workflows across multiple locations"],
      learnings: ["AI suggestions become useful when embedded in a reversible approval flow", "Multi-location products need authorization enforced at the data boundary"],
      snapshots: [
        {
          src: skiftAiSnap,
          alt: "Skift AI shift planner",
          caption: "AI Shift Planner — constraint-aware rota generation with staffing availability"
        },
        {
          src: skiftScheduleSnap,
          alt: "Skift weekly schedule grid",
          caption: "Weekly Calendar Matrix — bilingual rota with multi-location coverage"
        },
        {
          src: skiftDashboardSnap,
          alt: "Skift operations telemetry",
          caption: "Operations Overview — attendance kiosk, daily coordination, and labor telemetry"
        }
      ],
    },
    simulation: {
      scenario: "Generate Friday coverage for a location with a lunch peak and two employee constraints.",
      inputLabel: "Fixed staffing brief",
      input: "Friday 10:00–22:00 · lunch peak 11:30–14:00 · minimum 3 staff at peak · Maya max 8h · Elias unavailable after 17:00.",
      stages: ["Reading availability", "Estimating demand coverage", "Generating a draft rota", "Validating hours and breaks", "Publishing a reviewable schedule"],
      resultTitle: "Draft rota passes the selected checks",
      result: [
        { label: "Coverage", value: "3 staff across the lunch peak" },
        { label: "Constraint check", value: "Maya: 7.5h · Elias ends 16:30" },
        { label: "Warning", value: "Manager should confirm break placement before applying" },
        { label: "Status", value: "Draft — ready for manager review", tone: "positive" },
      ],
    },
  },
  {
    slug: "human-pose-estimation",
    name: "Human Pose Estimation",
    order: 4,
    featured: true,
    category: "Computer Vision",
    context: "Bachelor thesis · Biomechanics",
    summary: "A practical pipeline that turns ordinary cycling footage into 2D and 3D movement analysis.",
    statement: "What if a camera could become a biomechanics lab?",
    accent: "#ecc188",
    visual: { src: poseThumb, alt: "Human Pose Estimation 3D biomechanics kinematics", kind: "Computer Vision" },
    repository: { url: "https://github.com/itscharanteja/Human_Pose_Estimation_Streamlit", label: "View repository", status: "public" },
    stack: ["Python", "Streamlit", "AlphaPose", "MotionBERT", "Computer Vision"],
    caseStudy: {
      problem: "Biomechanics analysis can require specialist equipment, limiting access for coaches who begin with ordinary training footage.",
      audience: "Cyclists, coaches, and researchers exploring accessible movement analysis.",
      role: "Integrated AlphaPose and MotionBERT, added CPU-compatible inference, implemented temporal smoothing and biomechanics extraction, and built the Streamlit interface.",
      workflow: ["Upload a cycling video", "Detect 2D joints", "Estimate a temporally stable 3D pose", "Extract cycling biomechanics", "Review the visualized result"],
      architecture: ["Streamlit interaction layer", "AlphaPose 2D detector", "MotionBERT 3D estimator", "Custom biomechanics extraction scripts"],
      blueprint: {
        topology: "Two-Stage 2D→3D Vision & Biomechanics Pipeline",
        tiers: [
          {
            label: "Interface Layer (Python + Streamlit)",
            nodes: [
              { title: "Monocular Video Upload", sub: "MP4 / Image Frame Ingestion", tech: "python", edgeToNext: { label: "RGB Frames", dir: "forward" } },
              { title: "Streamlit Analysis Studio", sub: "Pipeline Controls & Playback", tech: "streamlit", primary: true },
              { title: "Coaching Telemetry View", sub: "3D Skeleton & Joint Angles", tech: "activity" },
            ],
          },
          {
            label: "Vision Pipeline (2D Detection & Temporal Processing)",
            nodes: [
              { title: "OpenCV Frame Sampler", sub: "Bounding Box & FPS Prep", tech: "opencv", edgeToNext: { label: "Frame Batch", dir: "forward" } },
              { title: "AlphaPose 2D Detector", sub: "Per-Frame Skeletal Keypoints", tech: "python", primary: true, edgeToNext: { label: "2D Keypoint Seq", dir: "forward" } },
              { title: "Temporal Smoother", sub: "Trajectory & Jitter Filtering", tech: "filter" },
            ],
          },
          {
            label: "3D Lifting & Biomechanics (MotionBERT + Kinematics)",
            nodes: [
              { title: "CPU / GPU Runtime", sub: "Device-Agnostic PyTorch", tech: "pytorch", edgeToNext: { label: "Tensor Execution", dir: "forward", dashed: true } },
              { title: "MotionBERT 3D Lifter", sub: "Spatio-Temporal Depth Estimation", tech: "brain", primary: true, edgeToNext: { label: "3D Joint Vectors", dir: "forward" } },
              { title: "Biomechanics Extractor", sub: "Knee, Hip & Torso Angles", tech: "activity" },
            ],
          },
        ],
        connectors: [
          [
            { label: "Decoded Video Stream", dir: "down" },
            { label: "Pipeline Orchestration", dir: "both", accent: true },
            { label: "Annotated Overlay", dir: "up" },
          ],
          [
            { label: "CUDA / CPU Fallback", dir: "down", dashed: true },
            { label: "2D→3D Temporal Lifting", dir: "both", accent: true },
            { label: "Kinematic Metrics", dir: "up" },
          ],
        ],
      },
      decisions: ["Support CPU inference where CUDA is unavailable", "Add temporal smoothing to reduce unstable 3D motion", "Expose the entire pipeline through one approachable interface"],
      constraints: ["Results depend on camera position and visible joints", "Video estimation is not a replacement for calibrated clinical equipment"],
      outcomes: ["Combines 2D detection, 3D estimation, and biomechanics extraction in one workflow", "Supports both images and video through the Streamlit interface"],
      learnings: ["Model output needs domain-specific interpretation to become useful", "Transparent limitations make experimental computer vision easier to evaluate"],
      snapshots: [
        {
          src: poseSnap,
          alt: "Streamlit analysis studio interface",
          caption: "Streamlit analysis studio displaying 2D keypoints and 3D spatio-temporal pose estimation"
        }
      ],
    },
    simulation: {
      scenario: "Analyze a fixed side-view cycling sequence for posture and joint movement.",
      inputLabel: "Fixed analysis sample",
      input: "Side-view cycling clip · 8 seconds · 30 FPS · rider and bicycle fully visible.",
      stages: ["Sampling video frames", "Detecting 2D joints", "Estimating the 3D sequence", "Smoothing temporal motion", "Extracting biomechanics observations"],
      resultTitle: "Movement sequence processed",
      result: [
        { label: "Frames", value: "240 analyzed" },
        { label: "Pose coverage", value: "Key joints visible across the selected sequence" },
        { label: "Observation", value: "Stable side-view tracking; review knee angle near the top of the pedal cycle." },
        { label: "Use", value: "Exploratory coaching insight — not a clinical measurement", tone: "caution" },
      ],
    },
  },
  {
    slug: "transcript-checklist-qa",
    name: "Transcript Checklist QA",
    order: 5,
    featured: false,
    category: "Quality Assurance AI",
    context: "Local build · Transcript evaluation",
    summary: "A concurrent evaluator that checks two-person transcripts against structured criteria with evidence and confidence.",
    statement: "Quality assurance should show its evidence.",
    accent: "#c5b7ff",
    visual: { src: transcriptThumb, alt: "Transcript Checklist QA evaluation workbench", kind: "Quality Assurance AI" },
    repository: { url: null, label: "Repository coming soon — local build", status: "coming-soon" },
    stack: ["Next.js", "TypeScript", "Mantine", "FastAPI", "Amazon Bedrock", "Nova Micro", "Nova Sonic", "WebSockets", "SSE"],
    caseStudy: {
      problem: "Reviewing long two-person conversations against detailed checklists is repetitive, slow, and difficult to audit without quoted evidence.",
      audience: "Quality teams reviewing structured consultations, calls, or interviews.",
      role: "Built the transcript evaluator, concurrent checklist pipeline, structured model output, streaming API, editable checklists, and live simulated-call workflow.",
      workflow: ["Capture or paste a transcript", "Load the selected checklist", "Evaluate criteria concurrently", "Stream decisions as they complete", "Review confidence and quoted evidence"],
      architecture: ["Next.js + Mantine interface", "FastAPI evaluation and live-call services", "Server-sent evaluation events", "Nova Micro or local Ollama structured output", "Nova Sonic live speech path"],
      blueprint: {
        topology: "Concurrent Fan-Out QA Evaluator (SSE + Live WebSocket Audio)",
        tiers: [
          {
            label: "Frontend (Next.js + Mantine QA Workbench)",
            nodes: [
              { title: "Transcript & Live Call Input", sub: "Two-Person Dialogue Capture", tech: "webspeech", edgeToNext: { label: "Speaker Turns", dir: "forward" } },
              { title: "Checklist Evaluation UI", sub: "Editable Criteria & Live Cards", tech: "mantine", primary: true },
              { title: "Evidence & Audit Readout", sub: "Quoted Spans & Confidence", tech: "audit" },
            ],
          },
          {
            label: "Backend (Python FastAPI Concurrent Engine)",
            nodes: [
              { title: "Turn Parser & Chunker", sub: "Speaker Attribution & Indexing", tech: "python", edgeToNext: { label: "Indexed Turns", dir: "both" } },
              { title: "Async Fan-Out Coordinator", sub: "Parallel Criterion Tasks + SSE", tech: "fastapi", primary: true, edgeToNext: { label: "Quote Matcher", dir: "both" } },
              { title: "Schema & Evidence Gate", sub: "YES / NO / UNCLEAR Validator", tech: "guardrails" },
            ],
          },
          {
            label: "AI & Speech Services (Amazon Bedrock / Local Ollama)",
            nodes: [
              { title: "Nova Sonic WebSocket", sub: "Bidirectional Live Speech Path", tech: "aws", edgeToNext: { label: "Live Transcript", dir: "forward", dashed: true } },
              { title: "Bedrock Nova Micro", sub: "Concurrent Criterion Reasoning", tech: "aws", primary: true, edgeToNext: { label: "Fallback Adapter", dir: "both", dashed: true } },
              { title: "Local Ollama Runtime", sub: "Offline Structured Evaluation", tech: "ollama" },
            ],
          },
        ],
        connectors: [
          [
            { label: "WebSocket Audio Stream", dir: "both", dashed: true },
            { label: "HTTP POST + SSE Stream", dir: "both", accent: true },
            { label: "Incremental Verdicts", dir: "up" },
          ],
          [
            { label: "Real-Time Turn Feed", dir: "up", dashed: true },
            { label: "Parallel Converse Calls", dir: "both", accent: true },
            { label: "Cited JSON Verdicts", dir: "up" },
          ],
        ],
      },
      decisions: ["Evaluate checklist items independently and concurrently", "Require yes/no/unclear plus confidence and evidence", "Support a local Ollama path as well as Bedrock"],
      constraints: ["Live speech and Bedrock evaluation require configured AWS access", "Automated QA remains review support rather than a final authority"],
      outcomes: ["Streams checklist results instead of waiting for the entire evaluation", "Keeps every decision tied to transcript evidence"],
      learnings: ["Concurrent evaluation improves perceived and actual latency", "Evidence-first outputs make model judgments easier to challenge"],
      snapshots: [
        {
          src: transcriptSnap,
          alt: "Transcript checklist QA evaluation interface",
          caption: "Live consultation QA workbench with concurrent criteria evaluation, quoted dialogue evidence, and confidence metrics"
        }
      ],
    },
    simulation: {
      scenario: "Evaluate a short consultation transcript against four communication-quality checks.",
      inputLabel: "Fixed consultation transcript",
      input: "Clinician: What brought you in today?\nPatient: I have had headaches for three days.\nClinician: I’m sorry to hear that. Do you have fever, weakness, or vision changes?\nPatient: No fever or weakness, but light bothers me.",
      stages: ["Parsing speaker turns", "Loading four checklist items", "Evaluating criteria concurrently", "Matching quoted evidence", "Streaming the final QA summary"],
      resultTitle: "Four checks evaluated with evidence",
      result: [
        { label: "Open question", value: "Yes · “What brought you in today?” · 98%" },
        { label: "Empathy", value: "Yes · “I’m sorry to hear that.” · 96%" },
        { label: "Red-flag screening", value: "Yes · fever, weakness, and vision changes checked · 94%" },
        { label: "Safety-net advice", value: "Unclear · no follow-up guidance appears in this excerpt · 91%", tone: "caution" },
      ],
    },
  },
  {
    slug: "job-tracker",
    name: "Job Tracker",
    order: 6,
    featured: false,
    category: "Local-First AI",
    context: "Personal workflow · Privacy-first automation",
    summary: "A local Kanban board whose background service reads job emails and updates applications without cloud AI.",
    statement: "Keep the search organized without sending it away.",
    accent: "#8fc7ff",
    visual: { src: jobThumb, alt: "Job Tracker on-device Kanban automation", kind: "Local-First AI" },
    repository: { url: "https://github.com/itscharanteja/job_tracker", label: "View repository", status: "public" },
    stack: ["React", "Vite", "Python", "FastAPI", "SQLite", "Gmail API", "Ollama"],
    caseStudy: {
      problem: "Job seekers receive status changes through email but must repeatedly copy them into a separate tracking system.",
      audience: "Individuals who want automatic application tracking while keeping their data and AI processing local.",
      role: "Built the Kanban interface, FastAPI daemon, Gmail read-only synchronization, local Ollama classifier, SQLite storage, and consent workflow.",
      workflow: ["Poll new Gmail messages", "Skip previously processed mail", "Classify locally with Ollama", "Extract company and status", "Create or move the Kanban card"],
      architecture: ["React + Vite board", "Local FastAPI background daemon", "Read-only Gmail API", "Local Ollama model", "SQLite database"],
      blueprint: {
        topology: "Local-First Background Daemon + On-Device LLM Pipeline",
        tiers: [
          {
            label: "Frontend (React + Vite Local Workspace)",
            nodes: [
              { title: "Privacy & OAuth Consent", sub: "Read-Only Inbox Controls", tech: "auth", edgeToNext: { label: "Sync Trigger", dir: "forward" } },
              { title: "Five-Stage Kanban Board", sub: "Drag-and-Drop & Manual Edit", tech: "react", primary: true },
              { title: "Source Thread Links", sub: "Direct Gmail Message Jump", tech: "gmail" },
            ],
          },
          {
            label: "Backend (Local Python FastAPI Daemon)",
            nodes: [
              { title: "Gmail OAuth Adapter", sub: "Scoped gmail.readonly Poller", tech: "gmail", edgeToNext: { label: "Unseen Mail", dir: "forward" } },
              { title: "FastAPI Sync Orchestrator", sub: "Deduplication & Stage Router", tech: "fastapi", primary: true, edgeToNext: { label: "State Write", dir: "both" } },
              { title: "Deduplication Ledger", sub: "Processed Message-ID Filter", tech: "python" },
            ],
          },
          {
            label: "On-Device AI & Storage (Ollama + SQLite)",
            nodes: [
              { title: "Zero-Cloud Boundary", sub: "100% Localhost Execution", tech: "privacy", edgeToNext: { label: "Local Prompt", dir: "forward", dashed: true } },
              { title: "Local Ollama Classifier", sub: "Company & Stage Extraction", tech: "ollama", primary: true, edgeToNext: { label: "Classified Card", dir: "forward" } },
              { title: "SQLite Local Database", sub: "Portable On-Disk Persistence", tech: "sqlite" },
            ],
          },
        ],
        connectors: [
          [
            { label: "OAuth Consent State", dir: "down" },
            { label: "Localhost REST API", dir: "both", accent: true },
            { label: "Synchronized Cards", dir: "up" },
          ],
          [
            { label: "Sanitized Email Snippet", dir: "down", dashed: true },
            { label: "Local Ollama Inference", dir: "both", accent: true },
            { label: "SQLite Upsert", dir: "both" },
          ],
        ],
      },
      decisions: ["Keep storage and classification on the user's machine", "Use read-only Gmail access", "Retain manual drag-and-drop and creation alongside automation"],
      constraints: ["The local daemon must remain running to watch the inbox", "Email wording can make automatic company matching ambiguous"],
      outcomes: ["Automatically maps confirmations, interviews, and rejections onto a five-stage board", "Links tracked cards back to the source Gmail thread"],
      learnings: ["Privacy can define the architecture rather than become a later feature", "Automation should coexist with clear manual controls"],
      snapshots: [
        {
          src: jobSnap,
          alt: "Job tracker local Kanban board",
          caption: "On-device Kanban board tracking application pipeline stages with direct Gmail source jumps"
        }
      ],
    },
    simulation: {
      scenario: "Process three fixed inbox events and update an existing application board.",
      inputLabel: "Fixed inbox batch",
      input: "1 application confirmation · 1 interview invitation · 1 newsletter marked irrelevant.",
      stages: ["Scanning the inbox batch", "Removing previously processed mail", "Classifying locally", "Extracting company and stage", "Updating the Kanban board"],
      resultTitle: "Board synchronized locally",
      result: [
        { label: "Northstar Labs", value: "Created in Applied" },
        { label: "Morrow Systems", value: "Moved from In Process to Interview" },
        { label: "Weekly newsletter", value: "Ignored as unrelated" },
        { label: "Privacy", value: "Simulation represents local-only classification", tone: "positive" },
      ],
    },
  },
  {
    slug: "ai-content-automation",
    name: "AI Content Automation Platform",
    order: 7,
    featured: false,
    category: "Full-Stack AI",
    context: "SaaS platform · Queue-based generation",
    summary: "A full-stack content system with team access, queued generation, usage limits, and multi-format exports.",
    statement: "Generation becomes valuable when the workflow around it works.",
    accent: "#a8d6ff",
    visual: { src: contentThumb, alt: "AI Content Automation queue engine", kind: "Full-Stack AI" },
    repository: { url: "https://github.com/itscharanteja/AI_Content_Engeine_SaaS", label: "View repository", status: "public" },
    stack: ["Next.js", "TypeScript", "Fastify", "Supabase", "Redis", "BullMQ", "Docker"],
    caseStudy: {
      problem: "Generating a draft is only one part of a content product; teams also need access control, reliable processing, usage tracking, and usable exports.",
      audience: "Content teams and organizations generating repeatable campaign assets.",
      role: "Built the monorepo's web, API, and worker services, organization access, asynchronous generation, usage tracking, and export paths.",
      workflow: ["Submit a structured brief", "Validate access and usage", "Queue the generation job", "Produce content variations", "Review and export"],
      architecture: ["Next.js web application", "Fastify API service", "Redis + BullMQ worker queue", "Supabase authentication and Postgres", "Containerized monorepo"],
      blueprint: {
        topology: "Multi-Tenant SaaS Monorepo + BullMQ Worker Queue",
        tiers: [
          {
            label: "Frontend (Next.js + TypeScript Workspace)",
            nodes: [
              { title: "Structured Campaign Brief", sub: "Audience, Channel & Tone Input", tech: "typescript", edgeToNext: { label: "Submit Brief", dir: "forward" } },
              { title: "Organization Studio UI", sub: "Live Queue Status & Variants", tech: "nextjs", primary: true },
              { title: "Multi-Format Export Bar", sub: "Download JSON, TXT & PDF", tech: "export" },
            ],
          },
          {
            label: "Backend API & Queue (Fastify + Redis + BullMQ)",
            nodes: [
              { title: "Bearer Auth & Quota Guard", sub: "Workspace Limits & Validation", tech: "auth", edgeToNext: { label: "Authorized Job", dir: "forward" } },
              { title: "Fastify API Gateway", sub: "Non-Blocking Job Dispatch", tech: "fastify", primary: true, edgeToNext: { label: "Enqueue Job", dir: "both" } },
              { title: "Redis + BullMQ Broker", sub: "Async Queue, Backoff & Retries", tech: "bullmq" },
            ],
          },
          {
            label: "Workers & Persistence (Node Workers + Supabase)",
            nodes: [
              { title: "Supabase Auth & Tenancy", sub: "Organization & Member Roles", tech: "supabase", edgeToNext: { label: "Usage Quota", dir: "both" } },
              { title: "Dedicated Generation Worker", sub: "Titles, Hooks, Posts & Hashtags", tech: "nodejs", primary: true, edgeToNext: { label: "Persist Bundle", dir: "forward" } },
              { title: "Supabase PostgreSQL", sub: "Campaign History & Export Store", tech: "postgresql" },
            ],
          },
        ],
        connectors: [
          [
            { label: "Bearer JWT", dir: "down" },
            { label: "HTTP POST + Job Polling", dir: "both", accent: true },
            { label: "Exportable Artifacts", dir: "up" },
          ],
          [
            { label: "Quota Verification", dir: "both" },
            { label: "BullMQ Worker Dispatch", dir: "both", accent: true },
            { label: "Persisted Deliverables", dir: "up" },
          ],
        ],
      },
      decisions: ["Move generation into asynchronous jobs", "Validate inputs and bearer tokens at the API boundary", "Treat teams and limits as product-level concepts"],
      constraints: ["Production generation depends on configured external model access", "Quality still depends on a clear, structured brief"],
      outcomes: ["Generates titles, hooks, scripts, captions, and hashtags", "Supports JSON, text, and PDF export formats"],
      learnings: ["Queue visibility matters to user trust", "A model call becomes a product only after ownership, limits, and recovery are designed"],
      snapshots: [
        {
          src: contentSnap,
          alt: "AI content automation run generator",
          caption: "SaaS generation workspace with structured creative brief, target audience settings, and queue engine"
        }
      ],
    },
    simulation: {
      scenario: "Turn a fixed product brief into a queued campaign content package.",
      inputLabel: "Fixed creative brief",
      input: "Launch a privacy-first job tracker for early-career engineers. Tone: direct and reassuring. Channel: LinkedIn.",
      stages: ["Validating the brief", "Checking workspace limits", "Enqueueing the generation job", "Producing content variations", "Preparing the export package"],
      resultTitle: "Campaign package generated",
      result: [
        { label: "Title", value: "Your job search, organized on your machine." },
        { label: "Hook", value: "Stop translating every inbox update into another spreadsheet row." },
        { label: "Deliverables", value: "3 titles · 3 hooks · 1 short post · 8 hashtags" },
        { label: "Export", value: "JSON, TXT, and PDF ready", tone: "positive" },
      ],
    },
  },
  {
    slug: "expensehive",
    name: "ExpenseHive",
    order: 8,
    featured: false,
    category: "Mobile Product",
    context: "React Native · Shared expenses",
    summary: "A mobile expense and group-management experience with automatic splitting and real-time balances.",
    statement: "Shared money should feel clear, not awkward.",
    accent: "#8de0cd",
    visual: { src: expenseThumb, alt: "ExpenseHive mobile group expense sharing and balance ledger", kind: "Mobile Product" },
    repository: { url: "https://github.com/itscharanteja/Expense_Hive", label: "View repository", status: "public" },
    stack: ["React Native", "Expo", "Firebase Auth", "Firestore", "Firebase Storage", "Async Storage"],
    caseStudy: {
      problem: "Groups need a shared source of truth for expenses, participants, and balances without repeated manual calculations.",
      audience: "Friends, households, and small groups splitting recurring or one-off costs.",
      role: "Built the React Native experience, Firebase authentication, group and member flows, expense splitting, and real-time balance updates.",
      workflow: ["Choose or create a group", "Record an expense", "Select participants", "Calculate each share", "Synchronize balances and history"],
      architecture: ["React Native + Expo client", "Firebase authentication", "Firestore real-time data", "Firebase and local storage"],
      blueprint: {
        topology: "Real-Time Reactive Mobile Client + Firestore Sync",
        tiers: [
          {
            label: "Mobile Client (React Native + Expo)",
            nodes: [
              { title: "Group & Member Roster", sub: "Create Groups & Invite Friends", tech: "users", edgeToNext: { label: "Select Members", dir: "forward" } },
              { title: "Expense Entry & Split UI", sub: "Quick Mobile Expense Logging", tech: "react", primary: true },
              { title: "Live Balance Summary", sub: "Who Owes Whom & History", tech: "ledger" },
            ],
          },
          {
            label: "Application Logic (Auth & Split Calculation)",
            nodes: [
              { title: "AsyncStorage Session", sub: "On-Device Token Persistence", tech: "mobile", edgeToNext: { label: "Cached UID", dir: "both" } },
              { title: "Split & Balance Engine", sub: "Deterministic Share Allocation", tech: "react", primary: true, edgeToNext: { label: "Net Deltas", dir: "forward" } },
              { title: "Settlement Ledger", sub: "Per-Member Credit & Debit Math", tech: "ledger" },
            ],
          },
          {
            label: "Cloud Services (Google Firebase)",
            nodes: [
              { title: "Firebase Authentication", sub: "Identity & Group Access Guard", tech: "firebase", edgeToNext: { label: "Verified UID", dir: "forward" } },
              { title: "Cloud Firestore Sync", sub: "Real-Time Document Listeners", tech: "firebase", primary: true, edgeToNext: { label: "Asset Refs", dir: "both" } },
              { title: "Firebase Cloud Storage", sub: "Receipts & Group Imagery", tech: "firebase" },
            ],
          },
        ],
        connectors: [
          [
            { label: "Cached Auth State", dir: "both", dashed: true },
            { label: "Expense & Participant Action", dir: "both", accent: true },
            { label: "Recomputed Balances", dir: "up" },
          ],
          [
            { label: "Auth Token Verification", dir: "both" },
            { label: "Firestore Snapshot Sync", dir: "both", accent: true },
            { label: "Live Ledger Updates", dir: "up" },
          ],
        ],
      },
      decisions: ["Keep balances synchronized as group expenses change", "Make group membership part of the expense flow", "Use a mobile-first interface for on-the-go entry"],
      constraints: ["The repository documents the product but does not expose a hosted mobile build", "Financial records require careful access rules in a production rollout"],
      outcomes: ["Supports personal and group expenses", "Maintains transaction history and real-time group balances"],
      learnings: ["The clarity of a split matters more than the calculation itself", "Real-time updates need equally clear ownership and edit states"],
      snapshots: [
        {
          src: expenseSnap,
          alt: "ExpenseHive mobile application interface",
          caption: "React Native mobile client with group expense rosters, split calculator, and Firestore sync"
        }
      ],
    },
    simulation: {
      scenario: "Split one fixed dinner expense across three friends and update the group balance.",
      inputLabel: "Fixed group expense",
      input: "Dinner · ₹3,600 · paid by Charan · split equally with Maya and Elias.",
      stages: ["Reading the expense", "Matching group members", "Calculating equal shares", "Synchronizing balances", "Preparing the settlement summary"],
      resultTitle: "Expense split and balances updated",
      result: [
        { label: "Share per person", value: "₹1,200" },
        { label: "Maya owes Charan", value: "₹1,200" },
        { label: "Elias owes Charan", value: "₹1,200" },
        { label: "Group status", value: "3 balances synchronized", tone: "positive" },
      ],
    },
  },
];

export const featuredProjects = projectCatalog.filter((project) => project.featured);

export function getProject(slug) {
  return projectCatalog.find((project) => project.slug === slug);
}
